import { LoyaltyCustomer, LoyaltyProduct, LoyaltyProgram, Tier } from "./types";
import { addToCart } from "./utils";

class CartLoyalty extends HTMLElement {
  customer?: LoyaltyCustomer;
  loyaltyData?: LoyaltyProgram;
  isLoggedIn = false;
  currentTier?: Tier;
  currentTierGift?: LoyaltyProduct;
  atcButton = document.createElement("button"); // settng up as dom node as its easier to manage event handlers
  errorElem?: HTMLElement | null

  connectedCallback() {
    // load in JSON data for component
    try {
      this.loyaltyData = JSON.parse(document.querySelector("[data-loyalty-settings]")?.innerHTML);
      this.customer = JSON.parse(this.querySelector("[data-loyalty-customer]")?.innerHTML || "{}");
      // testing data 
      // this.customer = {
      //   "id": 7929873137851,
      //   "total_spent": 250,
      //   "tags": ["loyalty-gift-Besties","swell_vip_pen pals"]
      // }
    } catch(e) {
      // error and hide component if there's an issue
      console.error(e);
      this.hideComponent();
    }

    this.errorElem = this.querySelector("[data-loyalty-error]");
    this.isLoggedIn = !!this.customer?.id;
    this.currentTier = this.getTier();
    this.currentTierGift = this.getGift()
    this.setupAtcButton();
    this.displayContent();
  }

  disconnectedCallback() {
    this?.atcButton?.removeEventListener("click", this.addGiftToCart)
  }

  /**
   * Setup styling of add to cart button and attach event handler
   */
  setupAtcButton() {
    this.atcButton.classList.add("btn", "button", "button--solid", "bg-brand-green", "text-brand-white", "py-2", "tracking-wide");
    this.atcButton.innerHTML = "CLAIM"
    this.atcButton.addEventListener("click", this.addGiftToCart.bind(this))
  }

  /**
   * Find the tier based on a customers total spend.
   * Note: the final tier doesn't have a "tier.to_threshold" property
   */
  getTier(): Tier | undefined { 
    if (!this.isLoggedIn || !this.customer || isNaN(this.customer?.total_spent)) return;
    const { total_spent } = this.customer

    try {
      return this.loyaltyData?.tiers.find(tier => tier?.to_threshold
        ? total_spent >= tier.from_threshold && total_spent <= tier.to_threshold
        : total_spent >= tier.from_threshold
      );
    } catch(e) {
      console.error("Loyalty data setup incorrectly, check customiser settings");
      this.hideComponent();
    }
  }

  /**
   * Match the gift by checking the ".cart__items" children in the sidebar
   */
  giftInCart() {
    const cartItems = document.querySelectorAll("[data-js-cart-item]");
    return !!Array.from(cartItems || []).find(item => item.dataset.productId == this.currentTierGift?.product_id)
  }

  /**
   * Retrieve the matching gift product data from the gift_products array, 
   * based on current tier
   */
  getGift(): LoyaltyProduct {
    return this.currentTier 
      ? this.loyaltyData?.gift_products?.find(gift => gift?.handle === this.currentTier?.gift_product) || null
      : null;
  }

  hasTierTag(tier: Tier | undefined) {
    return !!(tier?.tag && this.customer?.tags?.includes(tier.tag))
  }

  /**
   * Render the appropriate html content based on various states
   */
  displayContent() {
    const topImg = this.querySelector("[data-tier-image]");
    const content = this.querySelector("[data-tier-content]");
    const settings = this.loyaltyData?.settings
    // only display gifts if the customer qualifies and the gift is available for sale
    const shouldDisplayGift = this.hasTierTag(this.currentTier) && this.currentTierGift?.available && !this.giftInCart()

    topImg?.setAttribute("src", settings?.image || "");

    if (!content) return this.hideComponent();

    if (!this.isLoggedIn) {
      // logged out content
      content.innerHTML = this.renderLoggedOut();
    } else if (shouldDisplayGift) {
      // customer is in a certain tier and has the qualifying tag
      content.innerHTML = this.renderGiftHtml();
      content.querySelector(".loyalty-gift-wrapper")?.appendChild(this.atcButton);
    } else if (this.currentTier) {
      // tier data content
      content.innerHTML = this.renderRemainingHtml();
    } else {
      console.error("Tier states incorrect")
      this.hideComponent();
    }
  }

  async addGiftToCart() {
    if (!this.currentTierGift?.variant_id) {
      if (this.errorElem) this.errorElem.innerHTML =  "Error adding gift to cart";
      return
    }
    this.atcButton.setAttribute("disabled", "disabled");
    const result = await addToCart({ id: this.currentTierGift?.variant_id, qty: 1 });
    if (result?.status == 200) {
      this.hideComponent();
    } else {
      if (this.errorElem) this.errorElem.innerHTML =  "Error adding gift to cart";
      this.atcButton.removeAttribute("disabled");
    }
  }

  renderGiftHtml() {
    if (!this.currentTier || !this.currentTierGift?.available) return ""
    return /* html */`
      <div class="flex justify-between items-center gap-2 loyalty-gift-wrapper">
        <img src="${this.currentTier?.icon}" alt="${this.currentTier?.title}" width="40px" height="40px" />
        <p>You've got a free ${this.currentTier.title} gift!</p>
      </div>
    `
  }

  renderRemainingHtml() {
    if (!this.currentTier) return ""
    const hasNextTier = !!this.currentTier?.to_threshold;
    let html = this.currentTier.next_tier_content ? /* html */`<p>${this.currentTier.next_tier_content}</p>` : "";

    if (hasNextTier && this.customer) {
      const remaining = Math.max(this.currentTier.to_threshold, 0) - this.customer.total_spent + 1;
      html = /* html */`<p>${this.currentTier.next_tier_content?.replace("{amount}", `$${remaining}`) || ""}</p>`;
    }

    return html
  }

  renderLoggedOut() {
    return /* html */`
      <p class="mb-2">${this.loyaltyData?.settings?.logged_out_content || ""}</p>
      <div class="flex gap-8">
        <a href="/account/register" class="underline">Create Account</a>
        <a href="/account/login" class="underline">Login</a>
      </div>
    `
  }

  hideComponent() {
    this.classList.add("hidden");
  }
}

customElements.define("cart-loyalty", CartLoyalty);
